import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { useOverlayRegistry } from '../../context/OverlayContext';
import {
  modalPanelVariants,
  overlayVariants,
  reducedVariants,
} from '../../motion/tokens';
import usePrefersReducedMotion from '../../motion/usePrefersReducedMotion';

const ALIGN_CLASS = {
  center: 'items-center justify-center',
  start: 'items-start justify-center',
};

const SCRIM_CLASS = {
  dark: 'bg-black/55 backdrop-blur-sm',
  slate: 'bg-slate-900/55 backdrop-blur-sm',
  soft: 'bg-black/45 backdrop-blur-sm',
  blur: 'bg-black/55 backdrop-blur-sm',
};

const CLOSE_VARIANTS = {
  default:
    'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900',
  light:
    'border border-white/30 text-white/90 hover:bg-white/15 hover:text-white',
  ghost:
    'border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800',
};

export function ModalCloseButton({
  onClose,
  variant = 'default',
  className = '',
  label = 'Close',
}) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={label}
      className={`shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full transition-[transform,background-color,color] duration-150 hover:rotate-90 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${
        CLOSE_VARIANTS[variant] || CLOSE_VARIANTS.default
      } ${className}`.trim()}
    >
      <X className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

export function DialogPanel({
  children,
  className = '',
  maxWidth = 'max-w-lg',
  padded = false,
}) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-2xl border border-gray-100/80 w-full ${maxWidth} overflow-hidden flex flex-col max-h-[min(90vh,840px)] ${
        padded ? 'p-6 sm:p-8' : ''
      } ${className}`.trim()}
    >
      {children}
    </div>
  );
}

export function DialogHeader({
  title,
  subtitle,
  onClose,
  tone = 'default',
  closeVariant,
  className = '',
  titleId,
  sticky = true,
}) {
  const tones = {
    default: {
      wrap: 'bg-white border-b border-gray-100',
      title: 'text-gray-900',
      subtitle: 'text-gray-500',
      close: 'ghost',
    },
    muted: {
      wrap: 'bg-gradient-to-r from-slate-50 via-white to-slate-50 border-b border-gray-100',
      title: 'text-gray-900',
      subtitle: 'text-gray-500',
      close: 'ghost',
    },
    teal: {
      wrap: 'bg-gradient-to-r from-teal-500 to-teal-600 border-b border-teal-700/20',
      title: 'text-white',
      subtitle: 'text-teal-50/90',
      close: 'light',
    },
    soft: {
      wrap: 'bg-gradient-to-r from-teal-50 via-emerald-50 to-cyan-50 border-b border-teal-100',
      title: 'text-teal-950',
      subtitle: 'text-teal-700/80',
      close: 'ghost',
    },
  };
  const style = tones[tone] || tones.default;
  const resolvedClose = closeVariant || style.close;

  return (
    <div
      className={`flex items-start justify-between gap-4 px-5 py-3.5 sm:px-6 sm:py-4 shrink-0 ${
        sticky ? 'sticky top-0 z-10' : ''
      } ${style.wrap} ${className}`.trim()}
    >
      <div className="min-w-0 flex-1">
        <h2
          id={titleId}
          className={`text-base sm:text-lg font-semibold tracking-tight leading-snug ${style.title}`}
        >
          {title}
        </h2>
        {subtitle ? (
          <p className={`text-sm mt-0.5 leading-snug ${style.subtitle}`}>{subtitle}</p>
        ) : null}
      </div>
      {onClose ? (
        <ModalCloseButton onClose={onClose} variant={resolvedClose} />
      ) : null}
    </div>
  );
}

export function DialogFooter({ children, className = '' }) {
  return (
    <div
      className={`shrink-0 sticky bottom-0 z-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 px-5 py-3.5 sm:px-6 border-t border-gray-100 bg-gray-50/95 backdrop-blur-sm ${className}`.trim()}
    >
      {children}
    </div>
  );
}

export function DialogBody({ children, className = '' }) {
  return (
    <div className={`flex-1 min-h-0 overflow-y-auto ${className}`.trim()}>
      {children}
    </div>
  );
}

export default function Modal({
  open,
  onClose,
  children,
  align = 'center',
  scrim = 'blur',
  closeOnBackdrop = true,
  closeOnEscape = true,
  labelledBy,
  describedBy,
  className = '',
  contentClassName = '',
}) {
  const registry = useOverlayRegistry();
  const panelRef = useRef(null);
  const reduced = usePrefersReducedMotion();
  const scrimMotion = reducedVariants(overlayVariants, reduced);
  const panelMotion = reducedVariants(modalPanelVariants, reduced);

  useEffect(() => {
    if (!open || !registry) return undefined;
    return registry.registerOverlay();
  }, [open, registry]);

  useEffect(() => {
    if (!open || !closeOnEscape || !onClose) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose(event);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeOnEscape, onClose]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const previouslyFocused = document.activeElement;
    const focusable = panelRef.current.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === 'function') {
      focusable.focus();
    } else if (typeof panelRef.current.focus === 'function') {
      panelRef.current.focus();
    }
    return () => {
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  const alignClass = ALIGN_CLASS[align] || ALIGN_CLASS.center;
  const scrimClass = SCRIM_CLASS[scrim] || SCRIM_CLASS.blur;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          key="modal-overlay"
          className={`fixed inset-0 z-modal flex overflow-y-auto p-4 ${alignClass} ${
            align === 'start' ? 'pt-6 sm:pt-8' : ''
          } ${scrimClass} ${className}`.trim()}
          role="presentation"
          variants={scrimMotion}
          initial="hidden"
          animate="visible"
          exit="exit"
          onMouseDown={(event) => {
            if (
              closeOnBackdrop &&
              onClose &&
              event.target === event.currentTarget
            ) {
              onClose(event);
            }
          }}
        >
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-describedby={describedBy}
            tabIndex={-1}
            className={`outline-none w-full flex justify-center ${contentClassName}`.trim()}
            variants={panelMotion}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseDown={(event) => event.stopPropagation()}
          >
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
